import { GetCommentDto, GetEcgPermissionsDto, ICanRequestEcrOnDemandDtoRejectReason } from "../api/index.defs";
import urgentIcon from "../assets/ecgs/urgentActionIcon.svg";
import importantIcon from "../assets/ecgs/importantActionIcon.svg";
import priorityIcon from "../assets/ecgs/priorityActionIcon.svg";
import normalIcon from "../assets/ecgs/normalActionIcon.svg";
import defaultIcon from "../assets/ecgs/defaultActionIcon.svg";
import { EcgTableItem } from "../components/FilterableEcgTable/EcgsTable/EcgsTable";
import { ErrorConstants } from "../constants";
import { EcgEdit } from "../context/EcgDetailsContext";
import {
  EcgActionSeverity,
  EcgReviewState,
  EcrOnDemandDataRejectReasons,
  IEcg,
  IFindings,
  ILead,
  Note,
  UpdateEcg,
} from "../models/ecg.interface";
import { IFormattedMessage } from "../models/message.interface";
import { SaveInterpretedEcgFailureReason } from "../models/results/save-interpreted-ecg.interface";
import { getFullNameWithTitle } from "./userUtil";

export const getActionIcon = (severity: number = EcgActionSeverity.NA) => {
  switch (severity) {
    case EcgActionSeverity.Urgent:
      return urgentIcon;
    case EcgActionSeverity.Important:
      return importantIcon;
    case EcgActionSeverity.Normal:
      return normalIcon;
    case EcgActionSeverity.Priority:
      return priorityIcon;
    default:
      return defaultIcon
  }
};

export const getActionHeader = (severity: number = EcgActionSeverity.NA) => {
  switch (severity) {
    case EcgActionSeverity.Urgent:
      return 'Urgent';
    case EcgActionSeverity.Important:
      return 'Important';
    case EcgActionSeverity.Normal:
      return 'Feedback';
    case EcgActionSeverity.Priority:
      return 'Priority';
    default:
      return 'Feedback';
  }
}

export const getActionTextColor = (severity: number = EcgActionSeverity.NA) => {
  switch (severity) {
    case EcgActionSeverity.Urgent:
      return "#FF0000";
    case EcgActionSeverity.Important:
      return "#000";
    case EcgActionSeverity.Normal:
      return "#1AB200";
    case EcgActionSeverity.Priority:
      return "#E86B13";
    default:
      return "gray";
  }
};

export const formatSelectedActionText = (actionMessage: string) => {
  const lineDelimeter = "| ";
  const dotDelimeter = ". "

  const splitAction = actionMessage.split(lineDelimeter);

  if (splitAction.length === 1) {
    const actionMsgString = splitAction[0]
    const splitActionMsg = actionMsgString.split(dotDelimeter);
    const linesOfBoldText = [splitActionMsg[0]]
    const regularText = splitActionMsg[1] ? splitActionMsg.slice(1).join(dotDelimeter) : "";
    const linesOfRegularText = regularText.split(dotDelimeter);
    return { linesOfBoldText, linesOfRegularText }
  }

  const boldText = splitAction[0];
  const linesOfBoldText = boldText.split(dotDelimeter);
  const regularText = splitAction[1]
    ? splitAction.slice(1).join(lineDelimeter)
    : "";
  const linesOfRegularText = regularText.split(lineDelimeter);
  return { linesOfBoldText, linesOfRegularText };
};

export const mapEcgsToTableEcgs = (ecgs: IEcg[]): EcgTableItem[] => {
  return ecgs.map((ecg) => {
    const tableRow: EcgTableItem = {
      id: ecg.id,
      date: ecg.date,
      dateOfBirth: ecg.patient!.dateOfBirth ?? "",
      patientId: ecg.patient?.id ?? "",
      patientName: `${ecg.patient?.firstName ?? ""} ${ecg.patient?.lastName ??
        ""}`,
      orgName: ecg.organization?.rootName ?? "",
      entityName: ecg.organization?.name ?? "",
      entityPhone: ecg.organization?.phone ?? "",
      entityEmail: ecg.organization?.email ?? "",
      isUrgent: ecg.isUrgent,
      unviewedEcg: ecg.unviewedEcg,
      hasInterpretations: ecg.hasInterpretations,
      hasFindings: ecg.hasFindings,
      isReviewed: ecg.isReviewed,
      reference: ecg.reference,
      isApproved: ecg.isApproved,
      minutesRemaining: ecg.minutesRemaining,
      reviewPriority:
        typeof ecg.minutesRemaining !== "number"
          ? "none"
          : ecg.minutesRemaining <= 10
            ? "high"
            : "low",
      reviewType: ecg.reviewType,
      isSaved: ecg.isSaved,
      isArchived: ecg.isArchived,
      isUnreadable: ecg.isUnreadable,
      hasSymptoms: ecg.hasSymptoms,
      state: ecg.state && ecg.state.name,
      actionSeverity: ecg.actionSeverity,
    };

    return tableRow;
  });
};

type UpdateEcgWithoutId = Omit<UpdateEcg, "id">;

export const extractEnabledFromEdit = (
  ecgEdit: EcgEdit,
  enableFields: Partial<
    Omit<
      GetEcgPermissionsDto,
      | "findingsRequired"
      | "interpretationRequired"
      | "showSuggestiveOf"
      | "saveOnce"
      | "canEdit"
      | "enableNotes"
    >
  >
): UpdateEcgWithoutId => {
  let edit: UpdateEcgWithoutId = {};

  if (enableFields.enableActions) {
    edit.actions = ecgEdit.actions;
  }

  if (enableFields.enableFindings) {
    const findings = ecgEdit.findings
      ?.filter((finding) => finding.isEditable)
      .map((finding) => ({
        ...finding,
        leads: finding.useLeads
          ? finding.leads?.map((lead) => ({
            code: lead.code,
            value: lead.value,
            name: lead.name,
          }))
          : undefined,
      }));
    edit.findings = findings;
    edit.isReadable = ecgEdit.isReadable;
  }

  if (enableFields.enableInterpretation) {
    edit.interpretation = ecgEdit.interpretation;
  }

  edit.isUrgent = ecgEdit.isUrgent;

  return edit;
};

export const getSaveInterpretedEcgFailure = (
  errorCode: string
): SaveInterpretedEcgFailureReason => {
  if (
    errorCode !== ErrorConstants.SAVE_ECG_ALREADY_SAVED_BY_OTHER_REVIEWER_CODE
  )
    return SaveInterpretedEcgFailureReason.Unknown;

  return SaveInterpretedEcgFailureReason.EcgAlreadySaved;
};

export const getCheckedLeads = (leads: ILead[] = []) => {
  return leads?.filter((l) => l.value);
};

export const validateFindings = (
  findings: IFindings[] = []
): IFormattedMessage[] => {
  let invalidFindings: IFormattedMessage[] = [];

  const isFindingsExists = !!findings.filter((f) => f.value === true).length;

  const selectedFindingsWithLeads = findings.filter(
    (l) => l.useLeads && l.value
  );

  if (selectedFindingsWithLeads.length) {
    const invalidFindingsLeads = validateFindingsLeads(
      selectedFindingsWithLeads
    );

    if (invalidFindingsLeads.length) {
      invalidFindings = [...invalidFindings, ...invalidFindingsLeads];
    }
  } else if (!isFindingsExists) {
    // Validate at least 1 findings is selected
    invalidFindings.push({
      id: "ECG_DETAILS.FINDINGS",
    });
  }

  return invalidFindings;
};

export const validateFindingsLeads = (
  findingsWithLeads: IFindings[] = []
): IFormattedMessage[] => {
  const invalidFields: IFormattedMessage[] = [];
  // Validate findings with lead have at least 1 lead (of that finding) selected
  for (let i = 0; i < findingsWithLeads.length; i++) {
    const finding = findingsWithLeads[i];
    if (finding.leads) {
      // At lease 1 lead should be selected for this finding
      const isValid = finding.leads.some((lead) => lead.value);
      if (!isValid) {
        invalidFields.push({
          id: "ECG_DETAILS.FINDINGS.MISSING_LEADS",
          values: {
            findingName: finding.displayName,
          },
        });
      }
    }
  }

  return invalidFields;
};

export const mapToEcgNotes = (
  apiNotes?: GetCommentDto[]
): Note[] | undefined => {
  return apiNotes?.map(({ text, edit }) => ({
    text: text,
    date: edit.date ?? "",
    user: {
      id: edit.userId ?? "",
      display: getFullNameWithTitle(
        edit.salutation,
        edit.firstName,
        edit.lastName
      ),
    },
  }));
};

export const mapEcrOnDemandRejectReason = (rejectReason: ICanRequestEcrOnDemandDtoRejectReason) => {
  const messages = {
    [EcrOnDemandDataRejectReasons.None]: "",
    [EcrOnDemandDataRejectReasons.EcgUnreadable]: "The ECG was marked unreadable and cannot be sent for a Cardiologist Review.",
    [EcrOnDemandDataRejectReasons.MokedReviewInProcess]: "The Findings remain in process and upon completion you may order a cardiologist review.",
    [EcrOnDemandDataRejectReasons.EcrReviewInProcess]: "The Cardiologist Review is in process. Please wait to view the results.",
    [EcrOnDemandDataRejectReasons.EcgAlreadyReviewedByECR]: "The ECG has already been through a Cardiologist Review.",
    [EcrOnDemandDataRejectReasons.EcgAlreadyReviewedByCardiologist]: "The ECG has already been through internal Review.",
    [EcrOnDemandDataRejectReasons.MissingStateDefinition]: "Please contact customer service at custservice@shl-tm.com or 1-866-745-8356.",
    [EcrOnDemandDataRejectReasons.MissingReviewPermission]: "Please contact customer service at custservice@shl-tm.com or 1-866-745-8356.",
    [EcrOnDemandDataRejectReasons.MissingLicensedReviewer]: "Your state does not have a board-certified cardiologist at this time."
  }
  return messages[rejectReason] || 'Reject reason unknown'
}

export const isReviewInProgress = (ecgReviewState: EcgReviewState): boolean => [
  EcgReviewState.MokedReviewInProcess,
  EcgReviewState.ECRReviewInProcess,
].includes(ecgReviewState);